
// Custom hook for stable target generation management
import { useState, useCallback } from 'react';
import { isTargetGenerationRequest, getTargetTypeFromRequest } from '../../utils/targetGenerationUtils';

interface UseTargetGenerationReturn {
  isGenerating: boolean;
  generatingType: string | null;
  startGeneration: (message: string) => boolean;
  completeGeneration: () => void;
  resetGeneration: () => void;
}

export const useTargetGeneration = (): UseTargetGenerationReturn => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingType, setGeneratingType] = useState<string | null>(null);

  const startGeneration = useCallback((message: string): boolean => {
    if (isGenerating) {
      console.warn('🎯 useTargetGeneration: Generation already in progress, skipping');
      return false;
    }

    if (isTargetGenerationRequest(message)) {
      const targetType = getTargetTypeFromRequest(message);
      console.log('🎯 useTargetGeneration: Starting generation for type:', targetType);
      setIsGenerating(true);
      setGeneratingType(targetType);
      return true;
    }

    return false;
  }, [isGenerating]);

  const completeGeneration = useCallback(() => {
    console.log('🎯 useTargetGeneration: Completing generation');
    setIsGenerating(false);
    setGeneratingType(null);
  }, []);

  const resetGeneration = useCallback(() => {
    console.log('🎯 useTargetGeneration: Resetting generation state');
    setIsGenerating(false);
    setGeneratingType(null);
  }, []);

  return {
    isGenerating,
    generatingType,
    startGeneration,
    completeGeneration,
    resetGeneration
  };
};
